export type TokyoMovementSnapshot = {
  phase: string;
  turnNumber: number;
  currentPlayerId: string | null;
  tokyoCityId: string | null;
  tokyoBayId: string | null;
  playerNames: Record<string, string>;
  alive: Record<string, boolean>;
};

export type TokyoDeparture = {
  playerId: string;
  kickerId: string;
  turnNumber: number;
};

export type TokyoMovementEvent = {
  kind: "entered" | "left";
  title: string;
  detail: string;
};

function occupants(snapshot: TokyoMovementSnapshot) {
  return [snapshot.tokyoCityId, snapshot.tokyoBayId].filter((id): id is string => Boolean(id));
}

function readableNames(ids: string[], names: Record<string, string>) {
  const values = ids.map((id) => names[id] || "an opponent");
  if (values.length < 2) return values[0] || "an opponent";
  if (values.length === 2) return `${values[0]} and ${values[1]}`;
  return `${values.slice(0, -1).join(", ")}, and ${values.at(-1)}`;
}

export function detectTokyoMovement(
  previous: TokyoMovementSnapshot | null,
  current: TokyoMovementSnapshot | null,
  viewerId: string,
  rememberedDepartures: TokyoDeparture[] = [],
) {
  if (!previous || !current || current.turnNumber < previous.turnNumber) return { events: [] as TokyoMovementEvent[], departures: [] as TokyoDeparture[] };
  const previousOccupants = occupants(previous);
  const currentOccupants = occupants(current);
  const departedIds = previousOccupants.filter((id) => !currentOccupants.includes(id));
  const kickerId = previous.currentPlayerId || current.currentPlayerId;
  const isAttackResolution = previous.phase === "tokyo_yield" || current.phase === "tokyo_yield" || previous.phase === "tokyo_costume" || current.phase === "tokyo_costume" || (previous.phase === "tokyo_roll" && ["tokyo_costume", "tokyo_buy"].includes(current.phase));
  const newDepartures = isAttackResolution && kickerId
    ? departedIds.filter((id) => id !== kickerId).map((playerId) => ({ playerId, kickerId, turnNumber: current.turnNumber }))
    : [];
  let departures = [...rememberedDepartures.filter((item) => item.turnNumber >= current.turnNumber - 1), ...newDepartures]
    .filter((item, index, all) => all.findIndex((candidate) => candidate.playerId === item.playerId && candidate.kickerId === item.kickerId && candidate.turnNumber === item.turnNumber) === index);
  const events: TokyoMovementEvent[] = [];

  if (departedIds.includes(viewerId) && current.alive[viewerId] !== false && kickerId && kickerId !== viewerId) {
    events.push({
      kind: "left",
      title: "You've left Tokyo.",
      detail: `${current.playerNames[kickerId] || previous.playerNames[kickerId] || "An opponent"} kicked you out of Tokyo.`,
    });
  }

  if (!previousOccupants.includes(viewerId) && currentOccupants.includes(viewerId)) {
    const displaced = departures.filter((item) => item.kickerId === viewerId && item.turnNumber === current.turnNumber);
    if (displaced.length) {
      const displacedIds = [...new Set(displaced.map((item) => item.playerId))];
      events.push({
        kind: "entered",
        title: "You've entered Tokyo.",
        detail: `You kicked ${readableNames(displacedIds, current.playerNames)} out of Tokyo.`,
      });
      departures = departures.filter((item) => !displaced.includes(item));
    }
  }

  return { events, departures };
}
